import { defineStore } from 'pinia'
import { ref } from 'vue'
// Types
import type {
  QuestionEntity,
  NoneWordTarget,
  SentenceRepetitionTarget,
  ExpressiveVerbMorphologyTarget,
  SpeechSoundProductionTarget,
  WordOrPhraseMatch,
  reportingGroupKeys,
  TranscriptionResult
} from '@/customTypes'

// Services
import { DeepgramService } from '@/services/DeepgramService'
import type { DeepgramConnectionHandle } from '@/services/DeepgramService'
import Logger from '@/services/Logger.js'

import { useAudioAnalyserStore } from '@/stores/AudioAnalyserStore'

const logger: any = new Logger('LiveSpeechStore', true)

export type LateTranscriptEvent = {
  questionId: number
  subtestId: number | null
  result: TranscriptionResult
}

export type StartTranscriptionInput = {
  questionId: number
  subtestId: number | null
  onTranscript: (result: TranscriptionResult) => void
  onLateTranscript?: (event: LateTranscriptEvent) => void
  onError?: (error: unknown) => void
}

type ConnectionEntry = {
  questionId: number
  subtestId: number | null
  handle: DeepgramConnectionHandle
  openedAt: number
}

export const useLiveSpeechStore = defineStore('liveSpeech', () => {
  const audioAnalyserStore = useAudioAnalyserStore()
  const itemsToMatch = ref<WordOrPhraseMatch | null>(null)
  const connections = ref<Map<number, ConnectionEntry>>(new Map())
  const currentQuestionId = ref<number | null>(null)
  const answeredLiveIds = new Set<number>()
  const deepgramService = new DeepgramService()

  let currentTranscriptHandler:
    | ((result: TranscriptionResult) => void)
    | null = null
  let lateTranscriptHandler:
    | ((event: LateTranscriptEvent) => void)
    | null = null

  const DEEPGRAM_PROCESSOR_NAME = 'deepgramAudioProcessor'
  let processorAttached = false

  const setItemsToMatch = (items: WordOrPhraseMatch) => {
    itemsToMatch.value = items
  }

  const routeTranscript = (questionId: number, result: TranscriptionResult) => {
    if (questionId === currentQuestionId.value) {
      currentTranscriptHandler?.(result)
      return
    }
    if (answeredLiveIds.has(questionId)) return
    const entry = connections.value.get(questionId)
    lateTranscriptHandler?.({
      questionId,
      subtestId: entry?.subtestId ?? null,
      result
    })
  }

  const markAnsweredLive = (questionId: number) => {
    answeredLiveIds.add(questionId)
  }

  const ensureAudioProcessor = () => {
    if (processorAttached) return
    audioAnalyserStore.addCustomAudioProcessor(
      DEEPGRAM_PROCESSOR_NAME,
      (audioData: Float32Array) => {
        const activeId = currentQuestionId.value
        if (activeId === null) return
        const entry = connections.value.get(activeId)
        if (!entry) return
        deepgramService.sendAudioData(entry.handle.connection, audioData)
      }
    )
    processorAttached = true
  }

  const startTranscription = async ({
    questionId,
    subtestId,
    onTranscript,
    onLateTranscript,
    onError
  }: StartTranscriptionInput) => {
    if (!audioAnalyserStore.hasAnalyserSetUp) {
      throw new Error('Audio analyser not set up. Call setupAudioAnalyser first.')
    }

    currentTranscriptHandler = onTranscript
    if (onLateTranscript) {
      lateTranscriptHandler = onLateTranscript
    }

    try {
      // Practice retries reuse the same questionId — drop the old entry to avoid leaked listeners.
      if (connections.value.has(questionId)) {
        flushAndClose(questionId)
      }

      const connection = await deepgramService.createConnection(
        audioAnalyserStore.sampleRate,
        itemsToMatch.value ? itemsToMatch.value.keyWords : []
      )

      const handle = deepgramService.setupConnectionListeners(
        connection,
        questionId,
        routeTranscript,
        findMatches,
        onError
      )

      connections.value.set(questionId, {
        questionId,
        subtestId,
        handle,
        openedAt: Date.now()
      })
      currentQuestionId.value = questionId

      try {
        ensureAudioProcessor()
      } catch (processorError) {
        logger.error('Failed to attach Deepgram audio processor:', processorError)
        throw processorError
      }
    } catch (error) {
      logger.error('Error starting Deepgram transcription:', error)
      throw error
    }
  }

  // Stops feeding mic audio but leaves the socket open so late transcripts can still arrive.
  const detachAudioFeed = (questionId: number) => {
    if (currentQuestionId.value === questionId) {
      currentQuestionId.value = null
      currentTranscriptHandler = null
    }
  }

  const flushAndClose = (questionId: number) => {
    const entry = connections.value.get(questionId)
    if (!entry) return
    deepgramService.closeHandle(entry.handle)
    connections.value.delete(questionId)
    answeredLiveIds.delete(questionId)
    if (currentQuestionId.value === questionId) {
      currentQuestionId.value = null
      currentTranscriptHandler = null
    }
  }

  const closeAllExceptIds = (keep: Set<number>) => {
    for (const id of [...connections.value.keys()]) {
      if (!keep.has(id)) flushAndClose(id)
    }
  }

  const closeAll = () => {
    for (const id of [...connections.value.keys()]) {
      flushAndClose(id)
    }
    if (processorAttached) {
      audioAnalyserStore.removeCustomAudioProcessor(DEEPGRAM_PROCESSOR_NAME)
      processorAttached = false
    }
    answeredLiveIds.clear()
    lateTranscriptHandler = null
  }

  const setLateTranscriptHandler = (handler: ((event: LateTranscriptEvent) => void) | null) => {
    lateTranscriptHandler = handler
  }

  /**
   * Finds matches in the transcribed text based on the set word or phrases to match.
   * @param {string} text - The transcribed text to search for matches.
   * @returns {TranscriptionResult} The result containing the text and matches found.
   */
  const findMatches = (text: string): TranscriptionResult => {
    if (!itemsToMatch.value) return { text, matched: false }
    const words = text.toLowerCase().split(/\s+/)

    const item = itemsToMatch.value

    let matched: boolean
    let matchedText: string

    if (item.type === 'word') {
      const word = item.keyWords[0].toLowerCase()
      const alternativeMatches = item.alternatives ? item.alternatives[word] : []
      matched = words.some((w) => w === word || alternativeMatches?.includes(w))
      matchedText = matched
        ? words.find((w) => w === word || alternativeMatches?.includes(w)) || ''
        : ''
    } else {
      // phrase
      let keyWordIndex = 0
      let matchedWords: string[] = []
      const allKeyWordsMatched = true

      for (let i = 0; i < words.length; i++) {
        const word = words[i]
        const currentKeyWord = item.keyWords[keyWordIndex]?.toLowerCase()

        if (
          word === currentKeyWord ||
          (item.alternatives && item.alternatives[currentKeyWord]?.includes(word))
        ) {
          matchedWords.push(word)
          keyWordIndex++
          if (keyWordIndex === item.keyWords.length) break
        } else {
          keyWordIndex = 0
          matchedWords = []
        }
      }

      matched = keyWordIndex === item.keyWords.length
      matchedText = allKeyWordsMatched ? matchedWords.join(' ') : ''
    }

    return { text, matched, matchedText }
  }

  /**
   * Converts the current question target to Deepgram format.
   * @param {QuestionEntity} currentQuestion - The current question entity.
   * @param {reportingGroupKeys | undefined} subtestKey - The subtest key.
   * @returns {WordOrPhraseMatch | undefined} The converted target in Deepgram format.
   */
  const convertTargetFormat = (
    currentQuestion: QuestionEntity,
    subtestKey: reportingGroupKeys | undefined
  ): WordOrPhraseMatch | undefined => {
    const targets = currentQuestion.target || []
    if (targets.length === 0) return

    const firstTarget = targets[0]

    switch (subtestKey) {
      case 'nonword':
      case 'soundProduction': {
        return {
          type: 'word',
          keyWords: [(firstTarget as NoneWordTarget | SpeechSoundProductionTarget).target],
          alternatives: {
            [(firstTarget as NoneWordTarget | SpeechSoundProductionTarget).target]: [
              (firstTarget as NoneWordTarget | SpeechSoundProductionTarget).phoneticTarget || ''
            ]
          }
        }
      }
      case 'verbMorphology': {
        const mainTarget = (targets[0] as ExpressiveVerbMorphologyTarget).target
        const alternatives = targets
          .slice(1)
          .map((t) => (t as ExpressiveVerbMorphologyTarget).target)
        return {
          type: 'word',
          keyWords: [mainTarget],
          alternatives: { [mainTarget]: alternatives }
        }
      }
      case 'sentenceRepetition':
      default: {
        const words = (firstTarget as SentenceRepetitionTarget).target.split(' ')
        return {
          type: 'phrase',
          keyWords: words
        }
      }
    }
  }

  return {
    setItemsToMatch,
    startTranscription,
    detachAudioFeed,
    flushAndClose,
    closeAllExceptIds,
    closeAll,
    setLateTranscriptHandler,
    markAnsweredLive,
    convertTargetFormat,
    currentQuestionId,
    connections
  }
})
